import React from 'react'
import classNames from 'classnames'
import kebabCase from 'lodash.kebabcase'

import { mq, useMediaQuery } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import { HeadingBase } from 'marketing-site/src/mvp-v2/library/elements/HeadingBase/'
import { BackgroundBlobBlueAndOrange } from './elements/BackgroundBlob'

export const FeatureSpotlight: React.FC<IProps> = ({
  layout = 'media-right',
  icon,
  eyebrow,
  theme = 'none',
  title,
  subhead,
  ctas,
  media,
  mediaType,
  hasMediaShadow = true,
  backgroundColor = 'transparent',
  children,
}) => {
  const classes = classNames('feature-spotlight', 'grid-layout', {
    [`feature-spotlight--${layout}`]: layout,
    'feature-spotlight--has-additional-content': children,
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
    [`feature-spotlight--theme-${theme}`]: theme !== 'none',
  })

  const mediaTabletGridColumn = mediaType === 'image' ? '3/-3' : '2/-2'
  const isDesktop = useMediaQuery(`(${mq.desktop})`)

  return (
    <>
      <div className={classes} data-testid="feature-spotlight">
        <div className="feature-spotlight__inner">
          <header className="feature-spotlight__header">
            <HeadingBase
              icon={icon}
              theme={theme}
              alignment={isDesktop ? 'start' : 'center'}
              eyebrow={eyebrow}
              title={title}
              titleSize="heading-2"
              subhead={subhead}
              subheadSize="subhead-sm"
              listStyle="square"
            />
            {ctas && <div className="feature-spotlight__ctas">{ctas}</div>}
          </header>

          <div className="feature-spotlight__media">{media}</div>

          {children && <div className="feature-spotlight__additional-content">{children}</div>}
        </div>
        <BackgroundBlobBlueAndOrange />
      </div>

      <style jsx>
        {`
          .feature-spotlight {
            position: relative;

            &--theme-blue-gradient {
              .feature-spotlight__media {
                box-shadow: ${hasMediaShadow
                  ? '0px 0.8375px 0.8375px rgba(0, 87, 255, 0.04), 0px 10.05px 20.1px rgba(0, 87, 255, 0.16)'
                  : 'none'};
              }
            }
            &__inner {
              row-gap: 2.5rem;
              align-items: center;
              justify-items: center;
            }

            &__header,
            &__media,
            &__additional-content {
              grid-column: 1/-1;
            }

            &__header {
              text-align: center;
            }

            &__icon {
              margin-bottom: 1.25rem;
            }

            &__eyebrow {
              margin-bottom: 1rem;
            }

            &__subhead {
              margin-top: 1rem;

              :global(.rich-text > * + *) {
                margin-top: 1.25rem;
              }

              :global(.rich-text > ul li) {
                color: var(--mvp-slate);
                margin-bottom: 0.5rem;
                justify-content: center;
              }

              :global(.rich-text > ul li span) {
                color: var(--mvp-slate);
              }

              :global(.rich-text > br:last-child) {
                display: none;
              }
            }

            &__ctas {
              display: flex;
              flex-direction: column;
              column-gap: 1.5rem;
              row-gap: 1.25rem;
              justify-content: center;
              margin-top: 2rem;
            }

            &__media {
              display: flex;
              justify-content: center;
              border-radius: 15px;

              :global(.video-player-wrapper) {
                border-radius: 0;
              }

              & > :global(*),
              :global(picture),
              :global(img),
              :global(.lottie-video__animation),
              :global(.lottie-video__animation svg) {
                max-height: 36.625rem;
              }

              :global(.video-player-wrapper video) {
                max-height: 36.625rem;
                border-radius: 15px;
              }

              :global(.lottie-video__animation svg) {
                width: auto !important;
              }
            }

            &__additional-content {
              text-align: center;
            }
          }

          .mvp-background-color-slate,
          .mvp-background-color-blue,
          .mvp-background-color-deep-teal {
            .feature-spotlight__subhead {
              :global(.rich-text > ul li) {
                color: var(--paper);
              }

              :global(.rich-text > ul li span) {
                color: var(--paper);
              }
            }
          }

          @media (${mq.tablet}) {
            .feature-spotlight {
              &--theme-blue-gradient {
                .feature-spotlight__media {
                  box-shadow: ${hasMediaShadow
                    ? ' 0px 1px 1px rgba(0, 87, 255, 0.04), 0px 12px 24px rgba(0, 87, 255, 0.16)'
                    : 'none'};
                }
              }
              &__inner {
                row-gap: 3.5rem;
              }

              &__header {
                grid-column: 2/-2;
              }

              &__icon {
                margin-bottom: 1.5rem;
              }

              &__eyebrow {
                margin-bottom: 1.25rem;
              }

              &__subhead {
                :global(.rich-text > * + *) {
                  margin-top: 1.5rem;
                }
              }

              &__ctas {
                flex-direction: row;
                align-items: center;
                margin-top: 2.5rem;
              }

              &__media {
                grid-column: ${mediaTabletGridColumn};

                & > :global(*),
                :global(picture),
                :global(img),
                :global(.lottie-video__animation),
                :global(.lottie-video__animation svg) {
                  max-height: 43.75rem;
                }
              }

              &__additional-content {
                grid-column: 4/-4;
              }
            }
          }

          @media (${mq.desktop}) {
            .feature-spotlight {
              &__inner {
                grid-row-gap: 2.5rem;
              }

              &__header {
                grid-column: 1/7;
                grid-row: 1;
                text-align: left;
              }

              &__subhead {
                :global(.rich-text > ul li) {
                  justify-content: start;
                }
              }

              &__ctas {
                justify-content: start;
              }

              &__media {
                grid-column: 7/-1;
                grid-row: 1;
              }

              &__additional-content {
                grid-column: 1/6;
                grid-row: 2;
                margin-bottom: auto;
                text-align: left;
              }

              &--media-left {
                .feature-spotlight__header {
                  grid-column: 7/-1;
                }

                .feature-spotlight__media {
                  grid-column: 1/7;
                }

                .feature-spotlight__additional-content {
                  grid-column: 7/-2;
                }
              }

              &--has-additional-content {
                .feature-spotlight__header {
                  margin-top: auto;
                }

                .feature-spotlight__media {
                  grid-row: 1/3;
                }
              }
            }
          }

          @media (${mq.desktopLg}) {
            .feature-spotlight {
              &__header {
                grid-column: 1/6;
              }

              &__additional-content {
                grid-column: 1/5;
              }

              &--media-left {
                .feature-spotlight__header {
                  grid-column: 8/-1;
                }

                .feature-spotlight__media {
                  grid-column: 1/7;
                }

                .feature-spotlight__additional-content {
                  grid-column: 8/-2;
                }
              }
            }
          }
        `}
      </style>
    </>
  )
}
